import React, { useState, useEffect } from "react";
import sampleAvatar from "../../assets/sampleAvatar.png";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3080";

export default function NewChatModal({ token, currentUserId, onClose, onConversationCreated }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isGroup, setIsGroup] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Search users
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/getSearchResults`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ searchTerm: searchQuery }),
        });
        const data = await res.json();
        if (data.message === "success") {
          // Filter out current user
          setSearchResults(
            (data.searchResults || []).filter((u) => u.user_id !== currentUserId)
          );
        }
      } catch (err) {
        console.error("Error searching users:", err);
      }
      setLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery, token, currentUserId]);

  const toggleUser = (user) => {
    setSelectedUsers((prev) => {
      const exists = prev.find((u) => u.user_id === user.user_id);
      if (exists) {
        return prev.filter((u) => u.user_id !== user.user_id);
      }
      return [...prev, user];
    });
  };

  const handleCreate = async () => {
    if (selectedUsers.length === 0 || creating) return;

    const actualIsGroup = isGroup || selectedUsers.length > 1;

    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/createConversation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          participant_ids: selectedUsers.map((u) => u.user_id),
          is_group: actualIsGroup,
          group_name: actualIsGroup ? groupName || `Group (${selectedUsers.length + 1})` : undefined,
        }),
      });
      const data = await res.json();
      if (data.message === "success") {
        onConversationCreated(data.conversation_id);
      }
    } catch (err) {
      console.error("Error creating conversation:", err);
    }
    setCreating(false);
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ backgroundColor: "rgba(0,0,0,0.6)", zIndex: 9999 }}
      onClick={onClose}
    >
      <div
        className="rounded-4 d-flex flex-column"
        style={{
          backgroundColor: "#1e1e2e",
          border: "1px solid #2a2a3e",
          width: "90%",
          maxWidth: "440px",
          maxHeight: "80vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="d-flex align-items-center justify-content-between p-3 border-bottom" style={{ borderColor: "#2a2a3e !important" }}>
          <h6 className="text-white mb-0 fw-bold" style={{ fontSize: "1.6rem" }}>
            New Conversation
          </h6>
          <button
            className="btn btn-sm"
            onClick={onClose}
            style={{ color: "#828282", border: "none" }}
          >
            <ion-icon name="close" style={{ fontSize: "2rem" }}></ion-icon>
          </button>
        </div>

        {/* Selected users chips */}
        {selectedUsers.length > 0 && (
          <div className="d-flex flex-wrap gap-2 px-3 pt-3">
            {selectedUsers.map((user) => (
              <span
                key={user.user_id}
                className="badge rounded-pill d-flex align-items-center gap-1 px-2 py-1"
                style={{ backgroundColor: "var(--chat-purple)", cursor: "pointer", fontSize: "1.2rem" }}
                onClick={() => toggleUser(user)}
              >
                {user.fullname || user.username}
                <ion-icon name="close-circle" style={{ fontSize: "1.2rem" }}></ion-icon>
              </span>
            ))}
          </div>
        )}

        {/* Group toggle */}
        {selectedUsers.length > 1 && (
          <div className="px-3 pt-2">
            <input
              type="text"
              placeholder="Group name (optional)"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="border-0 rounded-pill px-3 w-100 text-white"
              style={{
                backgroundColor: "#2a2a3e",
                outline: "none",
                fontSize: "1.3rem",
                height: "36px",
              }}
            />
          </div>
        )}

        {/* Search input */}
        <div className="px-3 py-3">
          <div
            className="d-flex align-items-center rounded-pill px-3"
            style={{ backgroundColor: "#2a2a3e", height: "40px" }}
          >
            <ion-icon
              name="search-outline"
              style={{ color: "#828282", fontSize: "1.4rem" }}
            ></ion-icon>
            <input
              type="text"
              placeholder="Search people..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 bg-transparent text-white ms-2 w-100"
              style={{ outline: "none", fontSize: "1.4rem" }}
              autoFocus
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-grow-1 px-1" style={{ overflowY: "auto", maxHeight: "300px" }}>
          {loading && (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm text-secondary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          )}
          {searchResults.map((user) => {
            const isSelected = selectedUsers.some((u) => u.user_id === user.user_id);
            return (
              <div
                key={user.user_id}
                className="d-flex align-items-center px-3 py-2 gap-3"
                onClick={() => toggleUser(user)}
                style={{
                  cursor: "pointer",
                  backgroundColor: isSelected ? "rgba(93, 82, 255, 0.15)" : "transparent",
                  borderRadius: "8px",
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.05)";
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) e.currentTarget.style.backgroundColor = "transparent";
                }}
              >
                <div style={{ width: "40px", height: "40px", minWidth: "40px" }}>
                  <img
                    src={user.avatar && user.avatar !== "sampleAvatar.png" ? user.avatar : sampleAvatar}
                    alt=""
                    className="rounded-circle w-100 h-100"
                    style={{ objectFit: "cover" }}
                  />
                </div>
                <div className="flex-grow-1">
                  <div className="text-white" style={{ fontSize: "1.4rem" }}>
                    {user.fullname}
                  </div>
                  <div style={{ color: "#828282", fontSize: "1.2rem" }}>
                    @{user.username}
                  </div>
                </div>
                {isSelected && (
                  <ion-icon
                    name="checkmark-circle"
                    style={{ color: "var(--chat-purple)", fontSize: "2rem" }}
                  ></ion-icon>
                )}
              </div>
            );
          })}
          {searchQuery && !loading && searchResults.length === 0 && (
            <p className="text-center py-3" style={{ color: "#828282", fontSize: "1.3rem" }}>
              No users found
            </p>
          )}
        </div>

        {/* Create button */}
        <div className="p-3">
          <button
            className="btn w-100 rounded-pill text-white fw-bold"
            onClick={handleCreate}
            disabled={selectedUsers.length === 0 || creating}
            style={{
              backgroundColor: "var(--chat-purple)",
              border: "none",
              padding: "12px",
              fontSize: "1.4rem",
              opacity: selectedUsers.length === 0 ? 0.5 : 1,
            }}
          >
            {creating ? (
              <span className="spinner-border spinner-border-sm" role="status"></span>
            ) : selectedUsers.length > 1 ? (
              "Create Group"
            ) : (
              "Start Chat"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
